import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import './Monitoring.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Typography color="error" className="mon-error" sx={{ color: 'var(--error-color)' }}>
          Something went wrong: {this.state.error?.message || 'Unknown error'}
        </Typography>
      );
    }
    return this.props.children;
  }
}

const Monitoring = ({ theme = "dark" }) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Theme-aware colors for charts
  const themeColors = {
    dark: {
      error: '#ef4444',
      warning: '#f59e0b',
      success: '#10b981',
      primary: '#667eea',
      primaryOpaque: 'rgba(102, 126, 234, 0.7)',
      border: 'rgba(255, 255, 255, 0.2)',
      text: '#ffffff',
      hoverError: '#dc2626',
      hoverWarning: '#d97706',
      hoverSuccess: '#059669',
      hoverPrimary: '#5a67d8',
    },
    light: {
      error: '#dc2626',
      warning: '#d97706',
      success: '#059669',
      primary: '#3b82f6',
      primaryOpaque: 'rgba(59, 130, 246, 0.7)',
      border: 'rgba(0, 0, 0, 0.1)',
      text: '#1f2937',
      hoverError: '#b91c1c',
      hoverWarning: '#b45309',
      hoverSuccess: '#047857',
      hoverPrimary: '#2563eb',
    },
  };

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found.");
        
        const response = await fetch(`${API_BASE_URL}/alerts/`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Simply map the alerts without making additional API calls
        const formattedAlerts = data.map((alert) => ({
          id: alert.id.toString(),
          transactionId: alert.transaction_id.toString(),
          validationId: alert.validation_id, // Store this for later use
          alertType: alert.alert_type,
          details: alert.message.replace(/for transaction set \d+/i, '').trim(),
          evidence: 'Validation check failed',
          timestamp: new Date(alert.created_at).toLocaleString('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
          department: alert.message.includes('Documents') 
            ? 'Compliance' 
            : alert.message.includes('Invoice') 
            ? 'Financial' 
            : 'Audit',
          problemSolved: false, // Will be determined when viewing documents
          acknowledged: alert.aknowledged,
        }));
        
        setAlerts(formattedAlerts);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching alerts:', err);
        setError('Failed to load alerts: ' + (err.message || 'Unknown error'));
        setLoading(false);
        toast.error(
          err.message === 'No authentication token found.'
            ? 'Please log in to access alerts.'
            : 'Failed to load monitoring data.',
          {
            position: "bottom-right",
            autoClose: 3000,
            style: {
              background: theme === "dark" ? "#f8d7da" : "#fee2e2",
              color: theme === "dark" ? "#721c24" : "#991b1b",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 500,
            },
          }
        );
      }
    };

    fetchAlerts();
  }, [API_BASE_URL, theme]);

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const response = await fetch(`${API_BASE_URL}/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to acknowledge alert');
      }
      const updatedAlert = await response.json();
      
      // Update the alert in state to show it's acknowledged
      setAlerts(alerts.map(alert =>
        alert.id === alertId.toString()
          ? {
              ...alert,
              acknowledged: true,
              timestamp: new Date(updatedAlert.created_at).toLocaleString('en-US', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }),
            }
          : alert
      ));

      toast.success('Alert acknowledged successfully', {
        position: "bottom-right",
        autoClose: 3000,
        style: {
          background: theme === "dark" ? "#d1fae5" : "#d1fae5",
          color: theme === "dark" ? "#065f46" : "#065f46",
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: 500,
        },
      });

    } catch (err) {
      console.error('Error acknowledging alert:', err);
      toast.error(
        err.message === 'No authentication token found.'
          ? 'Please log in to acknowledge alerts.'
          : 'Failed to acknowledge alert: ' + (err.message || 'Unknown error'),
        {
          position: "bottom-right",
          autoClose: 3000,
          style: {
            background: theme === "dark" ? "#f8d7da" : "#fee2e2",
            color: theme === "dark" ? "#721c24" : "#991b1b",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 500,
          },
        }
      );
    }
  };

  const handleDeleteAlert = async (alertId, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      
      // Remove alert from local state immediately for better UX
      setAlerts(alerts.filter(alert => alert.id !== alertId.toString()));
      
      toast.success('Alert removed successfully', {
        position: "bottom-right",
        autoClose: 3000,
        style: {
          background: theme === "dark" ? "#d1fae5" : "#d1fae5",
          color: theme === "dark" ? "#065f46" : "#065f46",
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: 500,
        },
      });

    } catch (err) {
      console.error('Error deleting alert:', err);
      toast.error(
        err.message === 'No authentication token found.'
          ? 'Please log in to delete alerts.'
          : 'Failed to delete alert: ' + (err.message || 'Unknown error'),
        {
          position: "bottom-right",
          autoClose: 3000,
          style: {
            background: theme === "dark" ? "#f8d7da" : "#fee2e2",
            color: theme === "dark" ? "#721c24" : "#991b1b",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 500,
          },
        }
      );
    }
  };

  // Line Chart: Alerts Over Time
  const dates = [...new Set(alerts.map((a) => a.timestamp.split(',')[0]))].sort();
  const alertsByDate = dates.map((date) => ({
    date,
    count: alerts.filter((a) => a.timestamp.startsWith(date)).length,
  }));

  const lineData = {
    labels: dates,
    datasets: [
      {
        label: 'Alerts',
        data: alertsByDate.map((d) => d.count),
        fill: false,
        borderColor: themeColors[theme].primary,
        backgroundColor: themeColors[theme].primary,
        tension: 0.4,
        pointBackgroundColor: themeColors[theme].text,
        pointBorderColor: themeColors[theme].primary,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

// Bar Chart: Alerts by Department (excluding Financial)
  const departments = [...new Set(alerts.map((a) => a.department))].filter(dept => dept !== 'Financial');
  const alertsByDepartment = departments.map((dept) => ({
    department: dept,
    count: alerts.filter((a) => a.department === dept).length,
  }));

  const barData = {
    labels: departments,
    datasets: [
      {
        label: 'Alerts',
        data: alertsByDepartment.map((d) => d.count),
        backgroundColor: themeColors[theme].primaryOpaque,
        hoverBackgroundColor: themeColors[theme].hoverPrimary,
        borderColor: themeColors[theme].border,
        borderWidth: 1,
        borderRadius: 8,
      },
    ],
  };

  // Doughnut Chart: Alerts by Alert Type
  const alertTypes = [...new Set(alerts.map((a) => a.alertType))];
  const alertsByType = alertTypes.map((type) => ({
    type,
    count: alerts.filter((a) => a.alertType === type).length,
  }));

  const doughnutData = {
    labels: alertTypes,
    datasets: [
      {
        data: alertsByType.map((t) => t.count),
        backgroundColor: [
          themeColors[theme].primary,
          '#764ba2',
          '#f093fb',
          '#3b82f6',
          '#c084fc',
        ],
        hoverBackgroundColor: [
          themeColors[theme].hoverPrimary,
          '#6b21a8',
          '#db2777',
          '#2563eb',
          '#a855f7',
        ],
        borderColor: themeColors[theme].border,
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: themeColors[theme].text,
          font: { family: 'Inter', size: 12, weight: 600 },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: themeColors[theme].text,
        bodyColor: themeColors[theme].text,
        footerColor: themeColors[theme].text,
        borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: { family: 'Inter', size: 13, weight: 700 },
        bodyFont: { family: 'Inter', size: 12, weight: 500 },
      },
    },
    animation: {
      duration: 1200,
      easing: 'easeOutQuart',
    },
    scales: {
      x: {
        display: true,
        grid: { color: 'var(--border-color)', lineWidth: 1 },
        ticks: { 
          color: themeColors[theme].text,
          font: { family: 'Inter', size: 11, weight: 500 },
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'var(--border-color)', lineWidth: 1 },
        ticks: { 
          color: themeColors[theme].text,
          font: { family: 'Inter', size: 11, weight: 500 },
        },
      },
    },
  };

  const handleOpenDetails = (alert) => {
    setSelectedAlert(alert);
    setOpenDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setOpenDetailsModal(false);
    setSelectedAlert(null);
  };

  const handleViewDocuments = async (alert) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      
      // Fetch transactions for the transaction
      const response = await fetch(`${API_BASE_URL}/transactions`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch transactions');
      }
      
      const transactions = await response.json();
      const transaction = transactions.find(t => t.id.toString() === alert.transactionId);
      
      if (!transaction) {
        toast.error('Transaction not found', {
          position: "bottom-right",
          autoClose: 3000,
          style: {
            background: theme === "dark" ? "#f8d7da" : "#fee2e2",
            color: theme === "dark" ? "#721c24" : "#991b1b",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 500,
          },
        });
        return;
      }
      
      const documents = transaction.documents || [];
      
      if (documents.length === 0) {
        toast.warning('No documents found for this transaction', {
          position: "bottom-right",
          autoClose: 3000,
          style: {
            background: theme === "dark" ? "#fff3cd" : "#fef3c7",
            color: theme === "dark" ? "#856404" : "#92400e",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 500,
          },
        });
        return;
      }
      
      // Fetch validations for the transaction (only when viewing documents)
      let validations = [];
      let validationId = null;
      try {
        const validationResponse = await fetch(
          `${API_BASE_URL}/documents/validations/${alert.transactionId}`,
          {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        
        if (validationResponse.ok) {
          const validationData = await validationResponse.json();
          const allValidations = Array.isArray(validationData) ? validationData : [];
          
          // Format validations to match DocumentManager's expected format
          validations = allValidations.map((v) => ({
            id: v.id,
            transactionId: v.transaction_set_id,
            transactionName: transaction.name,
            checkName: v.validation_check || "Validation Details",
            status: v.status === 'FAILED' ? 'Fail' : v.status === 'PASSED' ? 'Pass' : v.status === 'NOT_APPLICABLE' ? 'Not Applicable' : v.status || 'N/A',
            lastRun: v.last_run || 'N/A',
            evidence: Array.isArray(v.reason_evidence)
              ? v.reason_evidence.join("; ")
              : v.reason_evidence?.discrepancies?.join("; ") || "",
            details: v.status === 'PASSED'
              ? "Validation passed successfully"
              : Array.isArray(v.reason_evidence)
              ? v.reason_evidence[0] || "Validation failed"
              : v.reason_evidence?.discrepancies?.[0] || "Validation failed",
            documentIds: v.document_ids || [],
            progress: 100,
            changeHistory: [],
            highlightFields: [],
            module: 3,
          }));
          
          // Use the validation_id from the alert
          validationId = alert.validationId;
        }
      } catch (err) {
        console.error('Error fetching validations:', err);
        // Continue without validations if fetch fails
      }
      
      // Close modal first
      handleCloseDetails();
      
      // Navigate to document-manager with BOTH query param and state
      navigate(`/document-manager?transactionId=${alert.transactionId}`, {
        state: {
          transactionId: alert.transactionId,
          transactionName: transaction.name,
          fromRuntimeValidation: true,
          documents: documents,
          validations: validations,
          validationId: validationId,
        },
      });
      
    } catch (err) {
      console.error('Error fetching documents:', err);
      toast.error(
        err.message === 'No authentication token found.'
          ? 'Please log in to view documents.'
          : 'Failed to load documents: ' + (err.message || 'Unknown error'),
        {
          position: "bottom-right",
          autoClose: 3000,
          style: {
            background: theme === "dark" ? "#f8d7da" : "#fee2e2",
            color: theme === "dark" ? "#721c24" : "#991b1b",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 500,
          },
        }
      );
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box className={`mon-monitoring-container ${theme}-theme`} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: 'var(--background-color)' }}>
        <CircularProgress sx={{ color: 'var(--primary-color)' }} size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={`mon-monitoring-container ${theme}-theme`} sx={{ p: 3, backgroundColor: 'var(--background-color)' }}>
        <Typography className="mon-error" sx={{ color: 'var(--error-color)', background: 'var(--error-background)' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <Box className={`mon-monitoring-container ${theme}-theme`} sx={{ backgroundColor: 'var(--background-color)' }}>
        <div className="mon-monitoring-particles">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="mon-monitoring-particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${15 + Math.random() * 10}s`,
              }}
            />
          ))}
        </div>

        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={theme}
        />

        <Box className="mon-content" sx={{ p: { xs: '16px', sm: '24px', md: '32px' } }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            className="mon-tabs"
            sx={{
              background: 'var(--card-background)',
              borderColor: 'var(--border-color)',
              mb: 3,
              '& .MuiTab-root': {
                color: 'var(--text-secondary)',
                '&:hover': { color: 'var(--text-color)', background: 'var(--primary-color-opaque)' },
                '&.Mui-selected': { color: 'var(--text-color)', background: 'var(--gradient-primary)' },
              },
              '& .MuiTabs-indicator': { display: 'none' },
            }}
          >
            <Tab label="Alert List" aria-label="View alert list" />
            <Tab label="Active Alerts" aria-label="View active alerts" />
            <Tab label="Insights" aria-label="View alert insights" />
          </Tabs>

          <Box className="mon-content-section">
            {activeTab === 0 && (
              <Box className="mon-alerts-section">
                <TableContainer 
                  component={Paper} 
                  className="mon-data-table" 
                  sx={{ 
                    background: 'var(--card-background)', 
                    borderColor: 'var(--border-color)',
                    height: '850px',
                    overflow: 'auto'
                  }}
                >
                  <Table sx={{ minWidth: 500 }} stickyHeader aria-label="alerts table">
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            background: 'var(--gradient-primary) !important',
                            color: 'var(--text-color) !important',
                            fontWeight: 700,
                            position: 'sticky',
                            top: 0,
                            zIndex: 10
                          }}
                        >
                          Alert Type
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            background: 'var(--gradient-primary) !important',
                            color: 'var(--text-color) !important',
                            fontWeight: 700,
                            position: 'sticky',
                            top: 0,
                            zIndex: 10
                          }}
                        >
                          Timestamp
                        </TableCell>
                        <TableCell 
                          align="center"
                          sx={{ 
                            background: 'var(--gradient-primary) !important',
                            color: 'var(--text-color) !important',
                            fontWeight: 700,
                            position: 'sticky',
                            top: 0,
                            zIndex: 10
                          }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alerts.map((alert) => (
                        <TableRow
                          key={alert.id}
                          sx={{ 
                            '&:hover': { background: 'var(--primary-color-opaque)' },
                            cursor: 'pointer'
                          }}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleOpenDetails(alert)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleOpenDetails(alert);
                            }
                          }}
                          aria-label={`View details for alert ${alert.alertType}`}
                        >
                          <TableCell sx={{ color: 'var(--text-color)' }}>{alert.alertType}</TableCell>
                          <TableCell sx={{ color: 'var(--text-color)' }}>{alert.timestamp}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                              <Button
                                size="small"
                                className="mon-action-btn mon-details-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDetails(alert);
                                }}
                                sx={{
                                  background: 'var(--gradient-primary)',
                                  color: 'var(--text-color)',
                                  '&:hover': { background: 'var(--gradient-primary)', transform: 'translateY(-2px) scale(1.05)', boxShadow: 'var(--box-shadow-hover)' },
                                  '&:disabled': { background: 'var(--disabled-background)', color: 'var(--disabled-text)' },
                                }}
                                aria-label={`View details for ${alert.alertType}`}
                              >
                                View Details
                              </Button>
                              <Button
                                size="small"
                                className="mon-action-btn mon-acknowledge-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!alert.acknowledged) {
                                    handleAcknowledgeAlert(alert.id);
                                  }
                                }}
                                disabled={alert.acknowledged}
                                sx={{
                                  background: alert.acknowledged ? 'var(--disabled-background)' : 'var(--gradient-primary)',
                                  color: alert.acknowledged ? 'var(--disabled-text)' : 'var(--text-color)',
                                  '&:hover': { 
                                    background: alert.acknowledged ? 'var(--disabled-background)' : 'var(--gradient-primary)', 
                                    transform: alert.acknowledged ? 'none' : 'translateY(-2px) scale(1.05)', 
                                    boxShadow: alert.acknowledged ? 'none' : 'var(--box-shadow-hover)' 
                                  },
                                  '&:disabled': { background: 'var(--disabled-background)', color: 'var(--disabled-text)', cursor: 'not-allowed' },
                                }}
                                aria-label={alert.acknowledged ? 'Alert already acknowledged' : `Acknowledge alert ${alert.alertType}`}
                              >
                                {alert.acknowledged ? 'Acknowledged' : 'Acknowledge'}
                              </Button>
                              {alert.acknowledged && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDeleteAlert(alert.id, e)}
                                  sx={{
                                    color: 'var(--error-color)',
                                    '&:hover': { 
                                      background: 'rgba(239, 68, 68, 0.1)',
                                      transform: 'scale(1.1)'
                                    },
                                  }}
                                  aria-label={`Delete alert ${alert.alertType}`}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {activeTab === 1 && (
              <Box className="mon-active-alerts-section">
                {alerts.length === 0 ? (
                  <Typography className="mon-active-alerts-placeholder" sx={{ background: 'var(--card-background)', borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
                    No active alerts found. All systems operating normally. ✅
                  </Typography>
                ) : (
                  <List className="mon-active-alerts-list">
                    {alerts.map((alert) => (
                      <ListItem
                        key={alert.id}
                        sx={{ 
                          background: 'var(--card-background)', 
                          borderColor: 'var(--border-color)', 
                          '&:hover': { background: 'var(--primary-color-opaque)' },
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2
                        }}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleOpenDetails(alert)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleOpenDetails(alert);
                          }
                        }}
                        aria-label={`View details for alert ${alert.alertType}`}
                      >
                        <ListItemText
                          primary={alert.alertType}
                          secondary={
                            <>
                              <strong>Department:</strong> {alert.department}
                              <br />
                              {alert.details}
                            </>
                          }
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text-color)' }}
                          secondaryTypographyProps={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                              size="small"
                              className="mon-action-btn mon-acknowledge-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!alert.acknowledged) {
                                  handleAcknowledgeAlert(alert.id);
                                }
                              }}
                              disabled={alert.acknowledged}
                              sx={{
                                background: alert.acknowledged ? 'var(--disabled-background)' : 'var(--gradient-primary)',
                                color: alert.acknowledged ? 'var(--disabled-text)' : 'var(--text-color)',
                                '&:hover': { 
                                  background: alert.acknowledged ? 'var(--disabled-background)' : 'var(--gradient-primary)', 
                                  transform: alert.acknowledged ? 'none' : 'translateY(-2px) scale(1.05)', 
                                  boxShadow: alert.acknowledged ? 'none' : 'var(--box-shadow-hover)' 
                                },
                                '&:disabled': { background: 'var(--disabled-background)', color: 'var(--disabled-text)', cursor: 'not-allowed' },
                              }}
                              aria-label={alert.acknowledged ? 'Alert already acknowledged' : `Acknowledge alert ${alert.alertType}`}
                            >
                              {alert.acknowledged ? 'Acknowledged' : 'Acknowledge'}
                            </Button>
                            {alert.acknowledged && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAlert(alert.id, e);
                                }}
                                sx={{
                                  color: 'var(--error-color)',
                                  '&:hover': { 
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    transform: 'scale(1.1)'
                                  },
                                }}
                                aria-label={`Delete alert ${alert.alertType}`}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {activeTab === 2 && (
              <Box className="mon-graphs-section">
                <Box className="mon-charts" sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(3, 1fr)', 
                  gap: '24px',
                  width: '100%'
                }}>
                  <Box className="mon-chart" sx={{ background: 'var(--card-background)', borderColor: 'var(--border-color)' }}>
                    <Typography variant="subtitle1" sx={{ color: 'var(--text-color)' }}>Alerts Over Time</Typography>
                    <div style={{ height: '250px' }}>
                      <Line data={lineData} options={chartOptions} />
                    </div>
                  </Box>
                  <Box className="mon-chart" sx={{ background: 'var(--card-background)', borderColor: 'var(--border-color)' }}>
                    <Typography variant="subtitle1" sx={{ color: 'var(--text-color)' }}>Alerts by Department</Typography>
                    <div style={{ height: '350px' }}>
                      <Bar data={barData} options={chartOptions} />
                    </div>
                  </Box>
                  <Box className="mon-chart" sx={{ background: 'var(--card-background)', borderColor: 'var(--border-color)' }}>
                    <Typography variant="subtitle1" sx={{ color: 'var(--text-color)' }}>Alerts by Type</Typography>
                    <div style={{ height: '350px' }}>
                      <Doughnut data={doughnutData} options={chartOptions} />
                    </div>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <Dialog open={openDetailsModal} onClose={handleCloseDetails} classes={{ paper: 'mon-dialog' }} maxWidth="sm" fullWidth>
            <DialogTitle className="mon-dialog-title" sx={{ color: 'var(--text-color)', background: 'var(--gradient-primary)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {selectedAlert?.alertType}
            </DialogTitle>
            <DialogContent className="mon-dialog-content" sx={{ background: 'var(--card-background)' }}>
              <Typography variant="body1" sx={{ color: 'var(--text-color)' }}>
                <strong>Department:</strong> {selectedAlert?.department}
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, color: 'var(--text-color)' }}>
                <strong>Details:</strong><br />{selectedAlert?.details}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'var(--text-color)' }}>
                <strong>Evidence:</strong> {selectedAlert?.evidence}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'var(--text-color)' }}>
                <strong>Timestamp:</strong> {selectedAlert?.timestamp}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'var(--text-color)' }}>
                <strong>Status:</strong> {selectedAlert?.acknowledged ? 'Acknowledged ✓' : 'Pending'}
              </Typography>
            </DialogContent>
            <DialogActions className="mon-dialog-actions" sx={{ background: 'var(--card-background)' }}>
              <Button 
                onClick={handleCloseDetails} 
                sx={{
                  background: 'var(--gradient-primary)',
                  color: 'var(--text-color)',
                  '&:hover': { 
                    background: 'var(--gradient-primary)', 
                    transform: 'translateY(-2px) scale(1.05)', 
                    boxShadow: 'var(--box-shadow-hover)' 
                  },
                  '&:disabled': { 
                    background: 'var(--disabled-background)', 
                    color: 'var(--disabled-text)' 
                  },
                }}
                aria-label="Close alert details dialog"
              >
                Close
              </Button>
              {selectedAlert?.alertType === 'VALIDATION_FAILED' ? (
                <Button
                  onClick={() => {
                    handleViewDocuments(selectedAlert);
                    handleCloseDetails();
                  }}
                  sx={{
                    background: 'var(--gradient-primary)',
                    color: 'var(--text-color)',
                    '&:hover': { 
                      background: 'var(--gradient-primary)', 
                      transform: 'translateY(-2px) scale(1.05)', 
                      boxShadow: 'var(--box-shadow-hover)' 
                    },
                  }}
                  aria-label="View documents for transaction"
                >
                  View Documents
                </Button>
              ) : null}
              {!selectedAlert?.acknowledged && (
                <Button
                  onClick={() => {
                    handleAcknowledgeAlert(selectedAlert?.id);
                    handleCloseDetails();
                  }}
                  sx={{
                    background: 'var(--gradient-primary)',
                    color: 'var(--text-color)',
                    '&:hover': { 
                      background: 'var(--gradient-primary)', 
                      transform: 'translateY(-2px) scale(1.05)', 
                      boxShadow: 'var(--box-shadow-hover)' 
                    },
                    '&:disabled': { 
                      background: 'var(--disabled-background)', 
                      color: 'var(--disabled-text)' 
                    },
                  }}
                  aria-label={`Acknowledge alert ${selectedAlert?.alertType}`}
                >
                  Acknowledge
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

export default Monitoring;